import { fmtNum, MAX_STEPS, numInput, parseNum, type ApprovalSettings, t } from '@procumi/shared';
import { useEffect, useState } from 'react';
import { useAsync } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { ErrorBox, Loading } from './StateBox';

interface StepDraft {
  value: string;
  approver: string;
}

const toDraft = (s: ApprovalSettings) => ({
  steps: s.steps.map((x) => ({ value: numInput(x.minValue), approver: x.approver })),
  sites: Object.fromEntries(s.sites.map((x) => [x.id, x.approverId ?? ''])),
});

/**
 * „Contul firmei → Aprobări”: ce comenzi cer aprobare și cine aprobă. Pachetul
 * hotărăște cât de mult se poate seta (vezi shared/approvals.ts); doar
 * administratorul schimbă, ceilalți văd regulile.
 */
export function ApprovalSection({
  toast,
  isAdmin,
  onChoosePlan,
}: {
  toast: Toast;
  isAdmin: boolean;
  onChoosePlan?: () => void;
}) {
  const { data, error, loading, reload } = useAsync(() => api.approvals(), [], 'approvals');
  const [steps, setSteps] = useState<StepDraft[]>([]);
  const [sites, setSites] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!data) return;
    const d = toDraft(data);
    setSteps(d.steps);
    setSites(d.sites);
  }, [data]);

  if (loading) return <Loading />;
  if (error || !data)
    return <ErrorBox error={error ?? t('Nu am putut încărca regulile.')} onRetry={reload} />;

  const multi = data.level === 'multi';
  if (data.level === 'none') {
    return (
      <section className="sheet pad approvals">
        <h2>{t('Aprobarea comenzilor')}</h2>
        <p className="hint">
          {t(
            'Comenzile de peste o anumită valoare trec întâi pe la directorul de achiziții sau la CFO, apoi se emit. Aprobările sunt incluse din pachetul Echipă; în Companie poți avea mai mulți pași și câte un aprobator pe fiecare șantier.',
          )}
        </p>
        {onChoosePlan ? (
          <div>
            <button className="btn primary" type="button" onClick={onChoosePlan}>
              {t('Alege pachetul')}
            </button>
          </div>
        ) : null}
      </section>
    );
  }

  const noApprovers = data.approvers.length === 0;
  const set = (i: number, patch: Partial<StepDraft>) =>
    setSteps(steps.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  const save = async () => {
    const parsed = steps.map((s) => ({ minValue: parseNum(s.value), approver: s.approver }));
    if (parsed.some((s) => s.minValue == null || s.minValue < 0)) {
      return toast(t('Scrie pragul fiecărui pas, în lei fără TVA'));
    }
    setBusy(true);
    try {
      await api.saveApprovals({
        steps: parsed.map((s) => ({ minValue: s.minValue!, approver: s.approver })),
        sites: multi ? Object.fromEntries(Object.entries(sites).map(([k, v]) => [k, v || null])) : {},
      });
      reload();
      toast(parsed.length ? t('Regulile de aprobare au fost salvate') : t('Comenzile se emit fără aprobare'));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const approverSelect = (
    value: string,
    onChange: (v: string) => void,
    label: string,
    allowSite: boolean,
  ) => (
    <select
      className="inp"
      value={value}
      aria-label={label}
      disabled={!isAdmin}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="any">{t('orice aprobator')}</option>
      {allowSite ? <option value="site">{t('aprobatorul șantierului')}</option> : null}
      {data.approvers.map((a) => (
        <option key={a.id} value={a.id}>
          {a.name}
        </option>
      ))}
    </select>
  );

  return (
    <>
      <section className="sheet pad approvals">
        <h2>{t('Aprobarea comenzilor')}</h2>
        <p className="hint">
          {multi
            ? t(
                'O comandă trece prin toți pașii al căror prag îl depășește, de la cel mai mic la cel mai mare. Fără pași, comenzile se emit direct.',
              )
            : t(
                'Comenzile peste prag se emit abia după ce le aprobă cineva cu rolul Aprobator. Fără prag, se emit direct.',
              )}
        </p>
        {noApprovers ? (
          <p className="read-warn" style={{ margin: 0 }}>
            {t('Nimeni din firmă nu are încă rolul Aprobator. Îl dai din fila „Echipa”.')}
          </p>
        ) : null}

        {steps.length ? (
          <ol className="appr-steps">
            {steps.map((s, i) => (
              <li key={i}>
                <span className="appr-n" aria-hidden="true">
                  {i + 1}
                </span>
                <label className="appr-field">
                  {t('Comenzile de peste')}
                  <span className="appr-money">
                    <input
                      className="inp"
                      inputMode="decimal"
                      value={s.value}
                      disabled={!isAdmin}
                      placeholder="10.000"
                      onChange={(e) => set(i, { value: e.target.value })}
                    />
                    {t('lei')}
                  </span>
                </label>
                <label className="appr-field">
                  {t('le aprobă')}
                  {approverSelect(
                    s.approver,
                    (v) => set(i, { approver: v }),
                    t('Cine aprobă pasul {0}', i + 1),
                    multi,
                  )}
                </label>
                {isAdmin ? (
                  <button
                    className="btn small"
                    type="button"
                    onClick={() => setSteps(steps.filter((_, j) => j !== i))}
                  >
                    {t('Scoate')}
                  </button>
                ) : null}
              </li>
            ))}
          </ol>
        ) : (
          <p className="appr-off">{t('Acum comenzile se emit fără aprobare.')}</p>
        )}

        {isAdmin ? (
          <div className="appr-acts">
            {steps.length < (multi ? MAX_STEPS : 1) ? (
              <button
                className="btn"
                type="button"
                onClick={() => {
                  const last = steps.length ? (parseNum(steps[steps.length - 1]!.value) ?? 0) : 0;
                  setSteps([
                    ...steps,
                    { value: steps.length ? fmtNum(last * 5) : '10.000', approver: 'any' },
                  ]);
                }}
              >
                {steps.length ? t('Adaugă un pas') : t('Cere aprobare peste un prag')}
              </button>
            ) : null}
            <button className="btn primary" type="button" disabled={busy} onClick={() => void save()}>
              {busy ? t('Se salvează…') : t('Salvează regulile')}
            </button>
          </div>
        ) : (
          <p className="hint" style={{ margin: 0 }}>
            {t('Regulile le schimbă un administrator al contului.')}
          </p>
        )}
        {!multi ? (
          <p className="hint" style={{ margin: 0 }}>
            {t('Pași pe valoare și aprobator pe fiecare șantier: în pachetul Companie.')}
          </p>
        ) : null}
      </section>

      {multi && data.sites.length ? (
        <section className="sheet pad approvals" style={{ marginTop: 'var(--space-6)' }}>
          <h2>{t('Aprobatorul fiecărui șantier')}</h2>
          <p className="hint">
            {t(
              'Pentru pașii „aprobatorul șantierului”. Un șantier fără aprobator trece la oricare aprobator.',
            )}
          </p>
          <ul className="appr-sites">
            {data.sites.map((site) => (
              <li key={site.id}>
                <span>{site.name}</span>
                <select
                  className="inp"
                  value={sites[site.id] ?? ''}
                  disabled={!isAdmin}
                  aria-label={t('Aprobatorul pentru {0}', site.name)}
                  onChange={(e) => setSites({ ...sites, [site.id]: e.target.value })}
                >
                  <option value="">{t('orice aprobator')}</option>
                  {data.approvers.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
          {isAdmin ? (
            <div className="appr-acts">
              <button className="btn primary" type="button" disabled={busy} onClick={() => void save()}>
                {busy ? t('Se salvează…') : t('Salvează')}
              </button>
            </div>
          ) : null}
        </section>
      ) : null}
    </>
  );
}
